/**
 * Error monitoring — Sentry, ERROR MONITORING ONLY.
 *
 * Jerry's decision, 2026-08-10. The following are deliberately NOT enabled and
 * must not be added without separate justification:
 *
 *   * Session Replay          — records what the user sees and types
 *   * performance tracing     — tracesSampleRate stays unset (0)
 *   * profiling               — profilesSampleRate stays unset (0)
 *   * broad user-content capture
 *
 * Each of those changes what leaves Brandon's phone. A construction memory
 * tool holds a client's private site conditions; sending replays or request
 * bodies to a third party is a product and privacy decision, not a default.
 *
 * `sendDefaultPii` is explicitly false, and no capture hook is installed that
 * would attach transcripts, photos, project names or any other product
 * content to an event.
 */

import * as Sentry from '@sentry/react';
import { readEnvironmentName, readSentryDsn } from './env';
import { classifyError } from './error-classification';

let initialised = false;

/**
 * Initialise monitoring if a DSN is configured.
 *
 * FAILS SAFE WHEN ABSENT, by requirement: no hosted Sentry project exists yet,
 * and creating one is Jerry's action. With no DSN this is a no-op and the app
 * runs normally — it does not warn on every load, and it never blocks startup.
 */
export function initMonitoring(): boolean {
  if (initialised) return true;

  const dsn = readSentryDsn();
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: readEnvironmentName(),

    // Errors only. Everything below is a deliberate exclusion, not an oversight.
    integrations: [],
    tracesSampleRate: 0,
    profilesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
  });

  initialised = true;
  return true;
}

/**
 * Report an UNEXPECTED error.
 *
 * Takes an explicit short context string rather than arbitrary product data,
 * so nothing Brandon captured can travel with an event by accident.
 *
 * **EXPECTED BUSINESS REFUSALS ARE NEVER REPORTED (H-2, closing I-007).**
 * Tapping Capture on a Closed Project, or saving a follow-up someone already
 * moved, are rules working correctly — the product already explains each one
 * to Brandon in his own words. PostgREST nonetheless delivers them as HTTP
 * 500, so without this gate a dashboard would fill with routine noise and a
 * real fault would be lost inside it. The gate is applied HERE, once, rather
 * than at the ~90 call sites, so no future caller has to remember it.
 *
 * **Only a STRUCTURED code can cause suppression** — see
 * `error-classification.ts`. Anything unrecognised is still reported.
 */
export function reportError(error: unknown, context: string): void {
  const classification = classifyError(error);

  if (classification.kind === 'business') {
    // A rule doing its job. Visible while developing, silent in the field.
    if (import.meta.env.DEV) {
      console.debug(`[${context}] handled business refusal: ${classification.code}`);
    }
    return;
  }

  if (initialised) {
    // **A BROKEN REPORTER MUST NOT BREAK THE APP.** Monitoring is an
    // observer; if the SDK throws — misconfigured, offline, rate limited —
    // the caller's own error handling must still run exactly as it would
    // with no monitoring at all.
    try {
      Sentry.captureException(error, {
        // Technical identifiers only. `code` is a SQLSTATE-style string such
        // as '23505'; it carries no product content.
        tags: { context, code: classification.code ?? 'none' },
      });
    } catch {
      // Deliberately swallowed. Nothing here may change app behaviour.
    }
  } else if (import.meta.env.DEV) {
    console.error(`[${context}]`, error);
  }
}
